/**
 * Conjunction watchlist, ported from loader-conjunctions.js. Space-Track CDMs
 * first (TCA, miss distance, Pc), CelesTrak SOCRATES as the fallback (HTML
 * table, no Pc). Events involving a loaded mission sort first; Pc classes at
 * >= 1e-3 (critical), >= 1e-4 (action), >= 1e-5 (watch); 30 rows; 6 h refresh.
 * The parsers are pure. parseSocrates reads the table with a small tag
 * tokenizer instead of DOMParser so it runs in the node test environment.
 */
import type { Mission } from '../types';
import { getSocrates, getSpaceTrackCdm, type CdmRecord } from './feeds';
import { useAwareness, type Conjunction, type ConjunctionSource, type ConjunctionsState } from './store';
import { errorMessage, startPolling } from './util';

export const CONJUNCTIONS_REFRESH_MS = 6 * 60 * 60 * 1000;
export const COUNTDOWN_TICK_MS = 1000;
export const CONJUNCTION_ROW_LIMIT = 30;
/** Industry red line for evaluating a collision-avoidance manoeuvre. */
export const ACTION_PC_THRESHOLD = 1e-4;
/** Events whose TCA passed more than this long ago drop off the list. */
export const PAST_GRACE_MS = 60 * 1000;

export type PcClass = 'critical' | 'action' | 'watch' | 'low';

export function pcClass(pc: number | null | undefined): PcClass | null {
  if (pc === null || pc === undefined || Number.isNaN(pc)) return null;
  if (pc >= 1e-3) return 'critical';
  if (pc >= ACTION_PC_THRESHOLD) return 'action';
  if (pc >= 1e-5) return 'watch';
  return 'low';
}

function num(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return NaN;
  return typeof value === 'number' ? value : parseFloat(value);
}

/** Space-Track cdm_public rows to conjunctions, dropping rows without both ids and a valid TCA; sorted by TCA. */
export function normaliseCdms(cdms: unknown): Conjunction[] {
  if (!Array.isArray(cdms)) return [];
  const out: Conjunction[] = [];
  for (const raw of cdms as CdmRecord[]) {
    if (!raw || typeof raw !== 'object') continue;
    let rangeKm = num(raw.MIN_RNG);
    // Legacy rows carry MISS_DISTANCE in metres.
    if (Number.isNaN(rangeKm) && raw.MISS_DISTANCE !== undefined && raw.MISS_DISTANCE !== null) {
      rangeKm = num(raw.MISS_DISTANCE) / 1000;
    }
    const tcaMs = raw.TCA ? new Date(raw.TCA + (raw.TCA.indexOf('Z') === -1 ? 'Z' : '')).getTime() : NaN;
    const norad1 = parseInt(String(raw.SAT_1_ID), 10);
    const norad2 = parseInt(String(raw.SAT_2_ID), 10);
    if (!norad1 || !norad2 || Number.isNaN(tcaMs)) continue;
    const pcRaw = raw.PC !== null && raw.PC !== undefined ? raw.PC : raw.MAX_COLLISION_PROBABILITY;
    out.push({
      norad1,
      name1: raw.SAT_1_NAME || raw.SAT1_OBJECT_NAME || '',
      norad2,
      name2: raw.SAT_2_NAME || raw.SAT2_OBJECT_NAME || '',
      tcaMs,
      rangeKm,
      relSpeedKmPerSec: raw.REL_SPEED !== undefined && raw.REL_SPEED !== null && raw.REL_SPEED !== '' ? num(raw.REL_SPEED) / 1000 : null,
      pc: pcRaw !== null && pcRaw !== undefined ? num(pcRaw) : null,
    });
  }
  out.sort((a, b) => a.tcaMs - b.tcaMs);
  return out;
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

/** Text content of an HTML fragment: tags removed, common entities decoded, whitespace collapsed. */
export function htmlText(fragment: string): string {
  return fragment
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (m) => ENTITIES[m] ?? m)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Cell texts of every table row in the document. */
export function tableRows(html: string): string[][] {
  const rows: string[][] = [];
  const rowRe = /<tr\b[^>]*>([\s\S]*?)<\/tr>/gi;
  let row: RegExpExecArray | null;
  while ((row = rowRe.exec(html)) !== null) {
    const cells: string[] = [];
    const cellRe = /<td\b[^>]*>([\s\S]*?)<\/td>/gi;
    let cell: RegExpExecArray | null;
    while ((cell = cellRe.exec(row[1])) !== null) cells.push(htmlText(cell[1]));
    rows.push(cells);
  }
  return rows;
}

/**
 * SOCRATES table rows: NORAD 1, name 1, NORAD 2, name 2, TCA, minimum range
 * (km). Rows with fewer than 6 cells, a first id below 1000 or an unparsable
 * TCA or range are skipped. No Pc. Sorted by TCA.
 */
export function parseSocrates(html: string): Conjunction[] {
  if (!html || typeof html !== 'string') return [];
  const out: Conjunction[] = [];
  for (const tds of tableRows(html)) {
    if (tds.length < 6) continue;
    const norad1 = parseInt(tds[0], 10);
    if (!norad1 || norad1 < 1000) continue;
    const norad2 = parseInt(tds[2], 10);
    if (!norad2) continue;
    const tcaMs = new Date(tds[4]).getTime();
    const rangeKm = parseFloat(tds[5]);
    if (Number.isNaN(tcaMs) || Number.isNaN(rangeKm)) continue;
    out.push({ norad1, name1: tds[1], norad2, name2: tds[3], tcaMs, rangeKm, relSpeedKmPerSec: null, pc: null });
  }
  out.sort((a, b) => a.tcaMs - b.tcaMs);
  return out;
}

/** NORAD id from TLE line 1 (columns 3 to 7). */
function noradFromLine1(line1: string | undefined): number | null {
  if (!line1 || !/^1\s/.test(line1)) return null;
  const id = parseInt(line1.substring(2, 7), 10);
  return id && !Number.isNaN(id) ? id : null;
}

/**
 * NORAD id -> display name for the loaded missions. Sources, in order:
 * noradId, the legacy single-string tle, then orbit.tle.line1 (this port's
 * TLE field; the original read mission.tle and mission.tleLine1 only).
 */
export function missionNoradMap(missions: Mission[]): Record<number, string> {
  const map: Record<number, string> = {};
  for (const m of missions) {
    let id: number | null = null;
    if (m.noradId) id = parseInt(String(m.noradId), 10);
    else if (m.tle && typeof m.tle === 'string') {
      const line1 = m.tle.split('\n').find((l) => /^1\s/.test(l));
      id = noradFromLine1(line1);
    } else if (m.orbit?.tle?.line1) id = noradFromLine1(m.orbit.tle.line1);
    if (id && !Number.isNaN(id)) map[id] = m.name || m.id || 'NORAD ' + id;
  }
  return map;
}

export interface OrderedConjunction {
  event: Conjunction;
  /** Name of the loaded mission involved, null otherwise. */
  involves: string | null;
}

/** Drop events more than a minute past TCA; events involving a loaded mission first, each group in TCA order. */
export function orderConjunctions(
  events: Conjunction[],
  noradMap: Record<number, string>,
  nowMs: number,
): OrderedConjunction[] {
  const matched: OrderedConjunction[] = [];
  const others: OrderedConjunction[] = [];
  for (const event of events) {
    if (event.tcaMs < nowMs - PAST_GRACE_MS) continue;
    const hit1 = noradMap[event.norad1];
    const hit2 = noradMap[event.norad2];
    if (hit1 || hit2) matched.push({ event, involves: hit1 ?? hit2 });
    else others.push({ event, involves: null });
  }
  return matched.concat(others);
}

/** Countdown text as the original formatted it: "2d 3h 4m", "3h 4m", "4m 5s" or "5s"; negative clamps to 0. */
export function formatConjunctionCountdown(totalSec: number): string {
  let sec = Math.max(0, Math.floor(totalSec));
  const d = Math.floor(sec / 86400);
  sec -= d * 86400;
  const h = Math.floor(sec / 3600);
  sec -= h * 3600;
  const m = Math.floor(sec / 60);
  sec -= m * 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

/** Space-Track first, SOCRATES on any failure (including missing credentials); both failing leaves source null. */
export async function refreshConjunctions(nowMs: () => number = Date.now): Promise<ConjunctionsState> {
  let events: Conjunction[] = [];
  let source: ConjunctionSource = null;
  let error: string | null = null;
  const cdm = await getSpaceTrackCdm();
  if (cdm.ok) {
    events = normaliseCdms(cdm.cdms);
    source = 'space-track';
  } else {
    console.warn('[conjunctions] Space-Track unavailable, falling back to SOCRATES:', cdm.message);
    try {
      events = parseSocrates(await getSocrates());
      source = 'socrates';
    } catch (err) {
      console.warn('[conjunctions] SOCRATES feed also unavailable:', err);
      error = `${cdm.message}; SOCRATES: ${errorMessage(err)}`;
    }
  }
  const state: ConjunctionsState = { events, source, error, updatedMs: nowMs() };
  useAwareness.getState().setConjunctions(state);
  return state;
}

/** Refresh now and every 6 hours until stopped; the list is cleared on stop. */
export function startConjunctions(): () => void {
  const stop = startPolling(() => {
    void refreshConjunctions();
  }, CONJUNCTIONS_REFRESH_MS);
  return () => {
    stop();
    useAwareness.getState().setConjunctions(null);
  };
}
